import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { signIn } from '../../redux/thunk';

import {
  Avatar,
  Checkbox,
  FormControlLabel,
  Button,
  Paper,
  TextField as MuiTextField,
  Typography,
  Alert as MuiAlert,
} from '@mui/material';
import { spacing } from '@mui/system';

import { selectAuthUser, selectAuthError, selectAuthLoadingState } from 'selectors';
import useAppNavigation from 'hooks/useAppNavigation';

const Alert = styled(MuiAlert)(spacing as any);

const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)}px;
  background: ${(props) => (props.theme.palette.mode === 'dark' ? '#141414' : '#ffffff')};
  border: 1px solid ${(props) => (props.theme.palette.mode === 'dark' ? '#1f1f1f' : '#e5e5e5')};
  border-radius: 12px;
  box-shadow: none;
  max-width: 480px;
  margin: 0 auto;

  ${(props) => props.theme.breakpoints.up('md')} {
    padding: ${(props) => props.theme.spacing(8)}px;
  }
`;

const BigAvatar = styled(Avatar)`
  width: 92px;
  height: 92px;
  text-align: center;
  margin: 0 auto ${(props) => props.theme.spacing(5)}px;
`;

function SignIn() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useSelector(selectAuthUser);

  /** Error and loading state */
  const authLoadingState = useSelector(selectAuthLoadingState);
  const authError = useSelector(selectAuthError);
  const { navigateToTeamsTable } = useAppNavigation();

  useEffect(() => {
    if (authUser && !authError.AUTHENTICATION_ERROR) {
      navigateToTeamsTable();
    }
  }, [authError.AUTHENTICATION_ERROR, authUser, history, navigateToTeamsTable]);

  return (
    <Wrapper>
      <Helmet title="Sign In" />
      {/* <BigAvatar alt="Lucy" src="/static/img/avatars/avatar-1.jpg" /> */}

      <Typography component="h1" variant="h4" align="center" gutterBottom>
        {/* Welcome back, Lucy! */}
      </Typography>
      <Typography component="h2" variant="body1" align="center">
        Sign in to your account to continue
      </Typography>

      <Formik
        initialValues={{
          email: '',
          password: '',
          submit: false,
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
          password: Yup.string().max(255).required('Password is required'),
        })}
        onSubmit={async (values) =>
          // TODO: Fix with Redux Toolkit configureStore migration
          dispatch(
            signIn({
              email: values.email,
              password: values.password,
              /** Triggering BE check for super admin status */
              is_super_admin: true,
            }) as any,
          )
        }
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
          <form noValidate onSubmit={handleSubmit}>
            {authError.AUTHENTICATION_ERROR && (
              <Alert sx={{ mt: 2, mb: 1 }} severity="warning">
                {authError.AUTHENTICATION_ERROR.message}.{' '}
                {`Error code: ${authError.AUTHENTICATION_ERROR.status}`}
              </Alert>
            )}
            <TextField
              type="email"
              name="email"
              label="Email Address"
              value={values.email}
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && errors.email}
              onBlur={handleBlur}
              onChange={handleChange}
              my={2}
            />
            <TextField
              type="password"
              name="password"
              label="Password"
              value={values.password}
              error={Boolean(touched.password && errors.password)}
              fullWidth
              helperText={touched.password && errors.password}
              onBlur={handleBlur}
              onChange={handleChange}
              my={2}
            />
            {/* <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Remember me"
            /> */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={authLoadingState.AUTHENTICATION_LOADING}
            >
              Sign in
            </Button>
            <Button onClick={() => navigate('/auth/reset-password')} fullWidth color="primary">
              Forgot password
            </Button>
          </form>
        )}
      </Formik>
    </Wrapper>
  );
}

export default SignIn;
